import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import BookCard from '../components/BookCard';
import { apiUrl } from '../api';

const SECTIONS = [
  {
    label: 'Indian & Spiritual',
    categories: [
      { id: 'indian mythology',  label: 'Indian Mythology',   emoji: '🕉️',  description: 'Puranas, epics, legends' },
      { id: 'mahabharata',       label: 'Mahabharata',         emoji: '⚔️',  description: 'The great Indian epic' },
      { id: 'ramayana',          label: 'Ramayana',            emoji: '🏹',  description: "Rama's journey" },
      { id: 'vedas',             label: 'Vedas & Upanishads',  emoji: '📿',  description: 'Ancient Hindu scriptures' },
      { id: 'ayurveda',          label: 'Ayurveda',            emoji: '🌿',  description: 'Ancient Indian medicine' },
      { id: 'vastu shastra',     label: 'Vastu Shastra',       emoji: '🏛️',  description: 'Hindu architecture & space' },
      { id: 'yoga',              label: 'Yoga & Meditation',   emoji: '🧘',  description: 'Mind, body, breath' },
      { id: 'sanskrit',          label: 'Sanskrit Literature', emoji: '🪷',  description: 'Classical Sanskrit texts' },
      { id: 'hinduism',          label: 'Hinduism',            emoji: '🔱',  description: 'Hindu philosophy & practice' },
      { id: 'buddhism',          label: 'Buddhism',            emoji: '☸️',  description: "Buddha's teachings" },
      { id: 'jainism',           label: 'Jainism',             emoji: '✋',  description: 'Jain philosophy & ethics' },
      { id: 'astrology',         label: 'Astrology',           emoji: '🔯',  description: 'Vedic & Western astrology' },
      { id: 'spirituality',      label: 'Spirituality',        emoji: '✨',  description: 'Inner awakening' },
      { id: 'tantra',            label: 'Tantra',              emoji: '🌀',  description: 'Tantric traditions' },
    ],
  },
  {
    label: 'Classic Literature',
    categories: [
      { id: 'fiction',           label: 'Fiction',             emoji: '📖',  description: 'Novels and short stories' },
      { id: 'science fiction',   label: 'Science Fiction',     emoji: '🚀',  description: 'Futuristic worlds' },
      { id: 'mystery',           label: 'Mystery',             emoji: '🔍',  description: 'Whodunits and thrillers' },
      { id: 'romance',           label: 'Romance',             emoji: '💕',  description: 'Love stories' },
      { id: 'adventure',         label: 'Adventure',           emoji: '🗺️',  description: 'Action and exploration' },
      { id: 'horror',            label: 'Horror',              emoji: '👻',  description: 'Dark and terrifying tales' },
      { id: 'poetry',            label: 'Poetry',              emoji: '✍️',  description: 'Verse and lyric' },
      { id: 'drama',             label: 'Drama',               emoji: '🎭',  description: 'Plays and scripts' },
      { id: 'children',          label: "Children's",          emoji: '🧒',  description: 'For young readers' },
      { id: 'humor',             label: 'Humor & Satire',      emoji: '😄',  description: 'Comedy and wit' },
    ],
  },
  {
    label: 'Knowledge & Ideas',
    categories: [
      { id: 'history',           label: 'History',             emoji: '🏛️',  description: 'Historical accounts' },
      { id: 'philosophy',        label: 'Philosophy',          emoji: '🤔',  description: 'Ideas and wisdom' },
      { id: 'science',           label: 'Science',             emoji: '🔬',  description: 'Natural sciences' },
      { id: 'psychology',        label: 'Psychology',          emoji: '🧠',  description: 'Mind and behaviour' },
      { id: 'self help',         label: 'Self Help',           emoji: '💪',  description: 'Personal growth' },
      { id: 'biography',         label: 'Biography',           emoji: '👤',  description: 'Life stories' },
      { id: 'travel',            label: 'Travel',              emoji: '🌍',  description: 'Exploration and journeys' },
      { id: 'economics',         label: 'Economics',           emoji: '📊',  description: 'Markets and trade' },
      { id: 'politics',          label: 'Politics',            emoji: '🏛️',  description: 'Government and power' },
      { id: 'religion',          label: 'Religion',            emoji: '🙏',  description: 'World religions' },
      { id: 'cooking',           label: 'Cooking',             emoji: '🍳',  description: 'Recipes and food' },
      { id: 'art',               label: 'Art & Music',         emoji: '🎨',  description: 'Creative expression' },
    ],
  },
];

// Flatten all categories for lookup
const ALL_CATEGORIES = SECTIONS.flatMap((s) => s.categories);

function SkeletonCard() {
  return (
    <div className="card overflow-hidden animate-pulse">
      <div className="skeleton-shimmer relative" style={{ paddingTop: '140%' }}>
        <div className="absolute inset-0 bg-primary/50" />
      </div>
      <div className="p-4 space-y-3">
        <div className="h-4 skeleton rounded w-full" />
        <div className="h-4 skeleton rounded w-3/4" />
        <div className="h-3 skeleton rounded w-1/2" />
        <div className="h-8 skeleton rounded-lg w-full mt-2" />
      </div>
    </div>
  );
}

function Browse() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const subjectParam = searchParams.get('subject') || 'fiction';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);

  const [selectedCategory, setSelectedCategory] = useState(subjectParam);
  const [books, setBooks] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(pageParam);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const BOOKS_PER_PAGE = 20;
  const totalPages = Math.ceil(totalCount / BOOKS_PER_PAGE);

  const updateUrl = useCallback(
    (cat, page) => {
      const params = new URLSearchParams();
      params.set('subject', cat);
      if (page > 1) params.set('page', String(page));
      navigate(`/browse?${params.toString()}`, { replace: true });
    },
    [navigate]
  );

  const fetchBooks = useCallback(async (cat, page) => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(apiUrl(`/api/books/browse?subject=${encodeURIComponent(cat)}&page=${page}`));
      const data = await res.json();
      if (!cancelled) {
        setBooks(data.results || []);
        setTotalCount(data.count || 0);
      }
    } catch (err) {
      if (!cancelled) {
        setError('Failed to load books. Please try again.');
        setBooks([]);
        setTotalCount(0);
      }
    } finally {
      if (!cancelled) setLoading(false);
    }

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetchBooks(selectedCategory, currentPage);
  }, [selectedCategory, currentPage, fetchBooks]);

  useEffect(() => {
    setSelectedCategory(subjectParam);
    setCurrentPage(pageParam);
  }, [subjectParam, pageParam]);

  const handleCategorySelect = (catId) => {
    setSelectedCategory(catId);
    setCurrentPage(1);
    updateUrl(catId, 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrl(selectedCategory, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeCat = ALL_CATEGORIES.find((c) => c.id === selectedCategory) || ALL_CATEGORIES[0];

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (currentPage > 4) pages.push('...');
    for (let i = Math.max(2, currentPage - 2); i <= Math.min(totalPages - 1, currentPage + 2); i++) {
      pages.push(i);
    }
    if (currentPage < totalPages - 3) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="py-10">
      <div className="page-container">
        {/* Header */}
        <div className="mb-8">
          <h1 className="section-title mb-2">Browse by Category</h1>
          <p className="text-gray-400 text-sm">
            From ancient Indian scriptures to modern classics — all free, all in one place
          </p>
        </div>

        {/* Category sections */}
        <div className="space-y-6 mb-10">
          {SECTIONS.map((section) => (
            <div key={section.label}>
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">
                {section.label}
              </h2>
              <div className="flex flex-wrap gap-2">
                {section.categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.id)}
                    title={cat.description}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium
                                border transition-all duration-200 ${
                                  selectedCategory === cat.id
                                    ? 'bg-accent border-accent text-white shadow-lg shadow-accent/30'
                                    : 'bg-primary border-border text-gray-300 hover:border-accent/60 hover:text-white hover:bg-accent/10'
                                }`}
                  >
                    <span role="img" aria-label={cat.label}>{cat.emoji}</span>
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Active category header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl" role="img" aria-label={activeCat.label}>
              {activeCat.emoji}
            </span>
            <div>
              <h2 className="text-xl font-bold text-white">{activeCat.label}</h2>
              <p className="text-gray-500 text-sm">{activeCat.description}</p>
            </div>
          </div>
          {!loading && totalCount > 0 && (
            <span className="text-gray-500 text-sm">
              {totalCount.toLocaleString()} books found
            </span>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-800/50 bg-red-900/10 p-6 text-center mb-8">
            <p className="text-red-400 font-medium">{error}</p>
            <button
              onClick={() => fetchBooks(selectedCategory, currentPage)}
              className="mt-3 btn-secondary text-sm"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading skeletons */}
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {Array.from({ length: 20 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* Books grid */}
        {!loading && books.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {books.map((book) => (
                <BookCard key={book.id} book={book} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12 flex-wrap">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg border border-border
                             text-sm text-gray-400 hover:text-white hover:border-accent/60
                             disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Prev
                </button>

                {getPageNumbers().map((page, idx) =>
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-gray-600">...</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 ${
                        currentPage === page
                          ? 'bg-accent text-white shadow-lg shadow-accent/30'
                          : 'border border-border text-gray-400 hover:text-white hover:border-accent/60'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg border border-border
                             text-sm text-gray-400 hover:text-white hover:border-accent/60
                             disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </>
        )}

        {/* Empty state */}
        {!loading && books.length === 0 && !error && (
          <div className="text-center py-24 text-gray-500">
            <span className="text-6xl block mb-4">📭</span>
            <p className="text-lg">No books found in this category.</p>
            <p className="text-sm mt-2">Try selecting a different genre above.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Browse;
